import { useRecordContext, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";

import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { CheckboxGroupInput } from "./CheckboxGroupInput";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceArrayInput } from "@/components/admin/reference-array-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { contactOptionText } from "@/components/atomic-crm/misc/ContactOption";
import { DuplicateWarning } from "./DuplicateWarning";
import { CLUBS_RESOURCE } from "./peopleDataProvider";
import {
  channelChoices,
  contactFieldsFor,
  originChoices,
  roleChoices,
} from "./choices";

const label = (field: string) => `tenax.people.fields.${field}`;

// "Dati Tenax" of a person (task 1.5, D-033): roles, and fields by role. A
// coach and the clubs where they teach are saved from this one form.
export const TenaxContactInputs = () => {
  const translate = useTranslate();
  const record = useRecordContext<{ consent_at?: string | null }>();
  const roles = useWatch({ name: "tenax_roles" }) as string[] | undefined;
  const hasConsent = useWatch({ name: "marketing_consent" }) as
    | boolean
    | undefined;
  const fields = contactFieldsFor(roles);

  return (
    <section className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("tenax.people.section")}
      </h6>
      <DuplicateWarning kind="contact" />
      <CheckboxGroupInput
        source="tenax_roles"
        label={label("roles")}
        choices={roleChoices}
      />

      {fields.coach && (
        <>
          <ReferenceArrayInput
            source="tenax_teaches_at"
            reference={CLUBS_RESOURCE}
          >
            <AutocompleteArrayInput
              label={label("teaches_at")}
              optionText="name"
              helperText={false}
            />
          </ReferenceArrayInput>
          <TextInput
            source="fitp_qualification"
            label={label("fitp_qualification")}
            helperText={false}
          />
        </>
      )}

      {fields.player && (
        <>
          <TextInput
            source="player_level"
            label={label("player_level")}
            helperText={false}
          />
          <ReferenceInput source="home_club_id" reference={CLUBS_RESOURCE}>
            <AutocompleteInput
              label={label("home_club_id")}
              optionText="name"
              helperText={false}
            />
          </ReferenceInput>
          <ReferenceInput
            source="coach_contact_id"
            reference="contacts_summary"
          >
            <AutocompleteInput
              label={label("coach_contact_id")}
              optionText={contactOptionText}
              helperText={false}
            />
          </ReferenceInput>
        </>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput
          source="instagram_handle"
          label={label("instagram_handle")}
          helperText={false}
          parse={(value: string) => value?.replace(/^@/, "").toLowerCase()}
        />
        <NumberInput
          source="instagram_followers"
          label={label("instagram_followers")}
          helperText={false}
        />
        <SelectInput
          source="origin"
          label={label("origin")}
          choices={originChoices}
          helperText={false}
        />
        <SelectInput
          source="channel"
          label={label("channel")}
          choices={channelChoices}
          helperText={false}
        />
        <TextInput
          source="fic_client_id"
          label={label("fic_client_id")}
          helperText={false}
        />
      </div>

      <BooleanInput
        source="marketing_consent"
        label={label("marketing_consent")}
        helperText={
          hasConsent && record?.consent_at
            ? translate(label("consent_at"), {
                date: new Date(record.consent_at).toLocaleDateString("it-IT"),
              })
            : false
        }
      />
      {hasConsent && (
        <TextInput
          source="consent_source"
          label={label("consent_source")}
          helperText={false}
        />
      )}
    </section>
  );
};
